'use client';

import { pick as _pick, get as _get } from 'lodash-es';
import { Formik } from 'formik';
import { useMutation, useQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Loading from '@/components/loading';
import Main from '@/components/main';
import ErrorMessage from '@/components/error-message';
import NotFound from '@/components/not-found';
import LoginRequired from '@/components/login-required';
import { accountNotificationsQuery } from '@/apollo/account';
import { myDiscordLinkQuery } from '@/apollo/discord';
import Notificationsform, { formSchema } from '@/components/account/notifications-form';
import { updateAccountNotificationPreferencesMutation } from '@/apollo/account';
import { processMutationError } from '@/utils/formik';
import { useAppContext } from '@/contexts/app';
import AccountHeader from '@/components/account/header';
import MaterialLink from '@/components/material-link';
import AccountGroupNotificationsList from '@/components/account/group-notifications-list';
import DiscordLinkSection from '@/components/account/discord-link-section';
import DiscordNotificationsSection from '@/components/account/discord-notifications-section';

const pickAccount = (user) => _pick(user, [
  'weeklyEventsReminder',
  'weekendEventsReminder',
  'emailMarketingPreference',
]);

const AccountNotificationsPageClient = () => {
  const { isLoggedIn } = useAppContext();

  const [updateAccountNotificationPreferences] = useMutation(updateAccountNotificationPreferencesMutation);
  const { data, error, loading } = useQuery(accountNotificationsQuery, {
    skip: !isLoggedIn,
    ssr: false,
  });
  const { data: discordData } = useQuery(myDiscordLinkQuery, {
    skip: !isLoggedIn,
    ssr: false,
  });
  const user = _get(data, 'me');
  const hasDiscordLink = !!discordData?.me?.discordLink;

  if (!isLoggedIn) { return <LoginRequired />; }
  if (loading) { return <Loading />; }
  if (error) { return <ErrorMessage message='Error loading user.' />; }
  if (!user) { return <NotFound />; }

  const { groupMembers } = user;

  return (
    <Main compact>
      <AccountHeader tab="notifications" />

      <Typography variant="h2">Notifications</Typography>

      <Formik
        initialValues={pickAccount(user)}
        validationSchema={formSchema}
        onSubmit={(values, actions) => {
          updateAccountNotificationPreferences({
            variables: {
              data: values,
            },
          }).then(() => {
            actions.resetForm({ values });
          }).catch((error) => {
            processMutationError(error, actions);
          });
        }}
      >
        <Notificationsform />
      </Formik>

      <Typography variant="h2">Calendar</Typography>
      <p>
        <MaterialLink href="/calendar/subscribe">Subscribe to calendar</MaterialLink> to see Everyday Rides events in your own calendar app.
      </p>

      {hasDiscordLink && (
        <>
          <Typography variant="h2">Discord</Typography>
          <DiscordLinkSection />
          <DiscordNotificationsSection />
        </>
      )}

      <Typography variant="h2">Your Groups</Typography>
      <AccountGroupNotificationsList
        groupMembers={groupMembers}
      />

      <Typography variant="h2">Your Following</Typography>
      <p>
        Visit <MaterialLink href="/people/following">your following list</MaterialLink> to manage notifications for individual people.
      </p>
    </Main>
  );
};

export default AccountNotificationsPageClient;
